import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import * as echarts from 'echarts/core';
import { LineChart, BarChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, DataZoomComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import type { EChartsType } from 'echarts/core';
import { TrendChartSeries, TrendChartWindowMode } from './ind-trend-chart.types';

/**
 * Tree-shaken registration — adding a feature means registering its component
 * too. Canvas over SVG: repaints are markedly cheaper during pan/zoom, which is
 * what this component is built for, on low/mid-range devices.
 */
echarts.use([LineChart, BarChart, GridComponent, TooltipComponent, DataZoomComponent, CanvasRenderer]);

/** Literals because the canvas renderer cannot resolve CSS custom properties. */
const GRID_COLOR = 'rgb(229, 231, 235)';
const AXIS_LABEL_COLOR = 'rgb(82, 82, 82)';
const AXIS_LABEL_FONT_SIZE = 12;

/** Zoom-in floor, so a pinch cannot bottom out where a line conveys nothing. */
const MIN_VISIBLE_CATEGORIES = 4;

/**
 * An axis tooltip lists every visible series, so a chart with many of them
 * grows one taller than its own host. `confine` then clips the overflow rather
 * than shrinking it, which drops rows silently — the reader sees a complete
 * list that isn't one. These bound it to a share of the host instead, and the
 * rows that don't fit are reported as a count.
 */
const TOOLTIP_MAX_HOST_FRACTION = 0.6;
const TOOLTIP_ROW_HEIGHT = 22;
const TOOLTIP_CHROME_HEIGHT = 48;
const TOOLTIP_MIN_ROWS = 3;

const HTML_ENTITIES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
};

/** Series names are API-supplied, and the tooltip is built as raw HTML. */
function escapeHtml(text: string): string {
  return String(text ?? '').replace(/[&<>"]/g, (character) => HTML_ENTITIES[character]);
}

/** zrender marks every event it derived from a touch, which is the only way to
 *  tell a tap from a click once it reaches the canvas. */
interface ZrClickEvent {
  offsetX: number;
  offsetY: number;
  zrByTouch?: boolean;
}

interface TooltipRow {
  seriesName?: string;
  value?: number | null;
  marker?: string;
  axisValueLabel?: string;
}

/** Nulls last, so a capped list spends its rows on buckets that have data. */
function byValueDescending(left: TooltipRow, right: TooltipRow): number {
  const leftValue = left.value ?? Number.NEGATIVE_INFINITY;
  const rightValue = right.value ?? Number.NEGATIVE_INFINITY;
  return leftValue === rightValue ? 0 : rightValue - leftValue;
}

function isPlottableValue(value: number | null | undefined): boolean {
  return value !== null && value !== undefined;
}

function hasPlottableNeighbour(values: (number | null)[], index: number): boolean {
  return isPlottableValue(values[index - 1]) || isPlottableValue(values[index + 1]);
}

/**
 * Generic categorical trend chart on Apache ECharts (Apache-2.0, unlike the
 * Highcharts implementation this replaced). Nothing here is Rx-specific.
 */
@Component({
  selector: 'ind-trend-chart',
  templateUrl: './ind-trend-chart.html',
  styleUrls: ['./ind-trend-chart.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IndTrendChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() series: TrendChartSeries[] = [];
  @Input() categories: string[] = [];
  @Input() height = 260;
  @Input() xAxisTitle = '';
  @Input() yAxisTitle = '';
  @Input() noDataText = '';

  @Input() windowMode: TrendChartWindowMode = 'none';
  /** Anchored to the most recent category. Omit to open on the full range. */
  @Input() initialVisibleCategories?: number;
  /**
   * Pan/zoom refresh rate in ms, or `null` for none. `null` updates per pointer
   * event, which is what makes the swipe feel smooth — ECharts otherwise
   * throttles to 20ms whenever animation is off, reading as steppy. Raise to
   * ~16 to cap at 60fps if a low-end device struggles.
   */
  @Input() panThrottleMs: number | null = null;
  /**
   * Caps ECharts' default of `window.devicePixelRatio`, the single largest cost
   * in a pan frame: pixel count scales quadratically, so a 3x display paints 9x
   * a 1x one and every pan tick repaints all of it. Lower to 1.5 or 1 to trade
   * crispness for smoothness. Read at init, so changing it re-creates the chart.
   */
  @Input() maxDevicePixelRatio = 2;
  /**
   * Smallest gap between y-axis split lines. Setting 1 forces whole-number
   * ticks, which suits only series that really are counts — on fractional data
   * every value between two integers lands on the same tick and flattens the
   * chart. `rx-trends` passes 1 because it rounds for display anyway.
   */
  @Input() yAxisMinInterval?: number;

  /**
   * Names the series the capped tooltip had no room for. Left unset the count
   * is dropped, so a caller with no i18n of its own is not forced to invent a
   * label — nothing here may reach for a translation itself.
   */
  @Input() tooltipOverflowLabel?: (hiddenCount: number) => string;

  /**
   * Hands every touch on the plot to the chart rather than the browser. The
   * host's default keeps vertical page scroll working, which costs the pinch:
   * `pan-y` lets the browser claim a two-finger gesture as a scroll, and once
   * it has, the chart's `preventDefault` is a no-op. Only safe where nothing
   * behind the chart needs to scroll — a full-screen presentation, not a card.
   */
  @Input() ownsTouchGestures = false;

  /**
   * The chart has settled, once only, so a caller can drop a placeholder.
   *
   * Settled means resolved, not necessarily drawn: a series with nothing to
   * plot emits this too, since "empty" is an answer and the caller would
   * otherwise wait for it forever. What does NOT emit is a render that could
   * not run yet — a host still at zero width, say, where a later
   * ResizeObserver callback does the work. Data arriving is a different moment
   * again.
   */
  @Output() painted = new EventEmitter<void>();

  @ViewChild('chartHost') private chartHost?: ElementRef<HTMLDivElement>;
  private hasPainted = false;

  private chart: EChartsType | null = null;
  private resizeObserver?: ResizeObserver;
  private activeDevicePixelRatio?: number;
  private lastCategoriesKey = '';
  private shownTooltipIndex: number | null = null;

  get hasData(): boolean {
    return (this.series ?? []).some(
      (item) => item.visible !== false && (item.data ?? []).some((value) => value !== null && value !== undefined),
    );
  }

  ngAfterViewInit(): void {
    this.render();
    // ECharts paints to a fixed-size canvas and never reflows on its own.
    if (typeof ResizeObserver !== 'undefined' && this.chartHost) {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.chart) {
          this.chart.resize();
        } else {
          // Host had no measurable width when render() last ran — a modal still
          // animating in. Without this retry the chart never appears.
          this.render();
        }
      });
      this.resizeObserver.observe(this.chartHost.nativeElement);
    }
  }

  ngOnChanges(): void {
    if (this.chartHost) {
      this.render();
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    // Holds a canvas and its own listeners; dropping the reference is not enough.
    this.chart?.dispose();
    this.chart = null;
  }

  private render(): void {
    const host = this.chartHost?.nativeElement;
    if (!host) return;

    if (!this.hasData) {
      this.chart?.dispose();
      this.chart = null;
      // Settled, just empty — the caller is waiting to drop its placeholder,
      // and "nothing to draw" is a final answer. Withholding this left a
      // no-data series showing a skeleton forever, waiting on a paint that
      // could never come. Deliberately not done for the two bail-outs below:
      // those are genuinely not-ready-yet and a later call does the work.
      this.emitPaintedOnce();
      return;
    }

    // A zero-width host yields a zero-size canvas that never recovers; let the
    // ResizeObserver call back once laid out.
    if (!this.chart && host.clientWidth === 0) {
      return;
    }

    // Read before anything below can dispose the instance. Only carried over on
    // an unchanged axis — weekly -> monthly is a different axis, where the
    // caller's initial window is the right answer.
    const categoriesKey = this.categoriesKey();
    const preservedWindow =
      this.chart && categoriesKey === this.lastCategoriesKey ? this.currentZoomWindow() : null;
    this.lastCategoriesKey = categoriesKey;

    const targetRatio = Math.min(window.devicePixelRatio ?? 1, this.maxDevicePixelRatio);
    // Fixed at construction, so a change means rebuilding rather than re-setting.
    if (this.chart && this.activeDevicePixelRatio !== targetRatio) {
      this.chart.dispose();
      this.chart = null;
    }

    if (!this.chart) {
      this.chart = echarts.init(host, undefined, { renderer: 'canvas', devicePixelRatio: targetRatio });
      this.activeDevicePixelRatio = targetRatio;
      // Once per instance — `on` accumulates listeners. Drops the axis tooltip's
      // nearest-point lookup and DOM repositioning from the pan path.
      this.chart.on('dataZoom', () => this.hideTooltip());
      this.chart.getZr().on('click', (event: ZrClickEvent) => this.onChartTap(event));
    }
    // Series are rebuilt wholesale on every change, so merging would leave a
    // hidden series behind. Cost is that dataZoom is rebuilt too, hence
    // passing the live window back in.
    // A rebuilt option means the tooltip's own state no longer describes what
    // is on screen — a category index can have moved, or stopped existing.
    this.shownTooltipIndex = null;
    this.chart.setOption(this.buildOption(preservedWindow), { notMerge: true });

    this.emitPaintedOnce();
  }

  private emitPaintedOnce(): void {
    if (this.hasPainted) return;
    this.hasPainted = true;
    this.painted.emit();
  }

  /** Whether a preserved pan/zoom window still means the same thing. */
  private categoriesKey(): string {
    const categories = this.categories ?? [];
    return `${categories.length}|${categories[0] ?? ''}|${categories[categories.length - 1] ?? ''}`;
  }

  private currentZoomWindow(): [number, number] | null {
    if (!this.chart) return null;
    const option = this.chart.getOption() as { dataZoom?: { start?: number; end?: number }[] };
    const zoom = option?.dataZoom?.[0];
    if (!zoom || typeof zoom.start !== 'number' || typeof zoom.end !== 'number') return null;
    return [zoom.start, zoom.end];
  }

  private buildOption(preservedWindow: [number, number] | null = null): echarts.EChartsCoreOption {
    return {
      animation: false,
      // `containLabel` sizes the grid around tick labels but not around a
      // rotated axis *name*, so that width is reserved by hand or the y-axis
      // title is clipped at the canvas edge.
      grid: { left: this.yAxisTitle ? 30 : 8, right: 8, top: 16, bottom: 28, containLabel: true },
      tooltip: {
        trigger: 'axis',
        confine: true,
        // Default 0.4s animates the tooltip's own position, so it lags the finger.
        transitionDuration: 0,
        formatter: (params: TooltipRow | TooltipRow[]) => this.formatTooltip(params),
      },
      xAxis: {
        type: 'category',
        data: this.categories,
        boundaryGap: false,
        name: this.xAxisTitle || undefined,
        // ECharts' default ('end') reads as a stray trailing label here.
        nameLocation: 'center',
        nameGap: 30,
        nameTextStyle: { fontSize: AXIS_LABEL_FONT_SIZE, color: AXIS_LABEL_COLOR },
        axisLine: { lineStyle: { color: GRID_COLOR } },
        axisTick: { show: false },
        // Drops colliding labels rather than rotating them — what keeps ~26
        // weekly buckets readable on a phone.
        axisLabel: { fontSize: AXIS_LABEL_FONT_SIZE, color: AXIS_LABEL_COLOR, hideOverlap: true },
      },
      yAxis: {
        type: 'value',
        name: this.yAxisTitle || undefined,
        // ECharts' default collided with the top tick label.
        nameLocation: 'middle',
        nameGap: 34,
        nameRotate: 90,
        nameTextStyle: { fontSize: AXIS_LABEL_FONT_SIZE, color: AXIS_LABEL_COLOR },
        minInterval: this.yAxisMinInterval,
        axisLine: { show: false },
        splitLine: { lineStyle: { color: GRID_COLOR } },
        axisLabel: { fontSize: AXIS_LABEL_FONT_SIZE, color: AXIS_LABEL_COLOR },
      },
      dataZoom: this.buildDataZoom(preservedWindow),
      series: this.buildSeries(),
    };
  }

  /**
   * Touch only, and only on the category already showing. An axis tooltip is
   * how the chart is read — tapping along the axis to compare periods has to
   * keep moving it — so a blanket dismiss-on-tap would cost two taps per
   * period. Desktop keeps hover, where a click that hid the tooltip would be
   * undone by the next mouse move anyway.
   */
  private onChartTap(event: ZrClickEvent): void {
    if (!event.zrByTouch || !this.chart) return;

    const point: [number, number] = [event.offsetX, event.offsetY];
    if (!this.chart.containPixel({ gridIndex: 0 }, point)) {
      this.hideTooltip();
      return;
    }

    // Typed as an array for the multi-axis finders; a single axis yields a scalar.
    const converted: number | number[] = this.chart.convertFromPixel({ xAxisIndex: 0 }, point);
    const tapped = Array.isArray(converted) ? converted[0] : converted;

    if (tapped === undefined || Number.isNaN(tapped) || tapped === this.shownTooltipIndex) {
      this.hideTooltip();
      return;
    }
    this.shownTooltipIndex = tapped;
  }

  private hideTooltip(): void {
    this.shownTooltipIndex = null;
    this.chart?.dispatchAction({ type: 'hideTip' });
  }

  /** How many rows fit before the tooltip starts hiding the chart it explains. */
  private tooltipMaxRows(): number {
    const budget = this.height * TOOLTIP_MAX_HOST_FRACTION - TOOLTIP_CHROME_HEIGHT;
    return Math.max(TOOLTIP_MIN_ROWS, Math.floor(budget / TOOLTIP_ROW_HEIGHT));
  }

  private formatTooltip(params: TooltipRow | TooltipRow[]): string {
    const rows = Array.isArray(params) ? params : [params];
    if (!rows.length) return '';

    const ranked = [...rows].sort(byValueDescending);
    const shown = ranked.slice(0, this.tooltipMaxRows());
    const hiddenCount = ranked.length - shown.length;

    const header = `<div style="margin-bottom:4px;font-weight:600">${escapeHtml(
      rows[0]?.axisValueLabel ?? '',
    )}</div>`;
    const body = shown
      .map(
        (row) =>
          `<div style="display:flex;align-items:center;gap:8px">` +
          `${row.marker ?? ''}<span style="flex:1 1 auto">${escapeHtml(row.seriesName ?? '')}</span>` +
          `<span style="font-weight:600">${
            row.value === null || row.value === undefined ? '-' : escapeHtml(`${row.value}`)
          }</span></div>`,
      )
      .join('');

    const overflow =
      hiddenCount > 0 && this.tooltipOverflowLabel
        ? `<div style="margin-top:4px;opacity:0.6">${escapeHtml(
            this.tooltipOverflowLabel(hiddenCount),
          )}</div>`
        : '';

    return header + body + overflow;
  }

  /** `type: 'inside'` is the built-in touch handler: drag pans, pinch zooms. */
  private buildDataZoom(preservedWindow: [number, number] | null): Record<string, unknown>[] {
    if (this.windowMode === 'none') return [];

    // A window the user panned to wins over the opening one, so a re-render
    // from something unrelated — a legend chip toggle — doesn't yank the view.
    const [start, end] = preservedWindow ?? this.initialWindowPercent();
    const total = this.categories?.length ?? 0;
    return [
      {
        type: 'inside',
        xAxisIndex: 0,
        start,
        end,
        zoomOnMouseWheel: true,
        moveOnMouseMove: true,
        // ECharts needs this off to treat a plain drag as panning.
        preventDefaultMouseMove: false,
        // Null skips the throttle wrapper entirely, refreshing per pointer event.
        throttle: this.panThrottleMs,
        realtime: true,
        // Default 'filter' re-partitions every series' data store per pan frame
        // (AxisProxy.filterData) and rescales the y-axis as points enter and
        // leave, which reads as the chart jumping under your finger.
        filterMode: 'none',
        // Clamped so a series shorter than the floor can still render.
        minValueSpan: total > 0 ? Math.min(MIN_VISIBLE_CATEGORIES, total) : undefined,
      },
    ];
  }

  /** Opening slice as ECharts start/end percentages, anchored to the newest bucket. */
  private initialWindowPercent(): [number, number] {
    const total = this.categories?.length ?? 0;
    if (total <= 1 || !this.initialVisibleCategories) return [0, 100];

    const visibleCount = Math.min(Math.max(this.initialVisibleCategories, 2), total);
    if (visibleCount >= total) return [0, 100];
    return [((total - visibleCount) / total) * 100, 100];
  }

  private buildSeries(): Record<string, unknown>[] {
    return (this.series ?? [])
      // ECharts has no per-series `visible` flag, so a hidden series is simply
      // not rendered. This filter is what makes the legend chips work.
      .filter((item) => item.visible !== false)
      .map((item) => ({
        type: 'line',
        name: item.name,
        data: item.data,
        color: item.color,
        showSymbol: true,
        showAllSymbol: true,
        symbol: (_value: unknown, params: { dataIndex: number }) =>
          hasPlottableNeighbour(item.data ?? [], params.dataIndex) ? 'none' : 'circle',
        symbolSize: 8,
        lineStyle: { width: 2, type: item.lineStyleType ?? 'solid' },
        // Gaps for null buckets rather than bridging them.
        connectNulls: false,
      }));
  }
}
